import {
  type CaregiverRelation,
  hasUnidentifiedSpeakers,
  type SpeakerRole,
  visitSpeakersSchema,
} from "@rosc/contracts";
import type { JSX } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { toast } from "@/hooks/use-toast";
import { referenceClient } from "@/lib/orpc/client";
import { useVisitScope } from "@/lib/visit-queries";

// Speaker roles of a visit's transcripts. Roles live in the transcript labels
// ("Speaker 1 [Caregiver (mother)]:"), so the visit reads already carry them;
// this context only identifies unlabelled speakers once and saves the
// clinician's changes, then refreshes the visit reads.
export type VisitSpeakersValue = {
  identifying: boolean;
  // `${recordingPublicId ?? "main"}:${speaker}` while that change is saving.
  saving: string | null;
  setRole?: (change: {
    recordingPublicId: string | null;
    speaker: number;
    role: SpeakerRole;
    relation?: CaregiverRelation | null;
  }) => Promise<void>;
};

const VisitSpeakersContext = createContext<VisitSpeakersValue | null>(null);
export const useVisitSpeakers = (): VisitSpeakersValue | null =>
  useContext(VisitSpeakersContext);

// One automatic identification per visit and transcript set per page load,
// so a provider failure is not retried on every render or refresh.
const attempted = new Set<string>();
const isBusy = (status: string | null | undefined) =>
  /^(processing|generating|pending)$/i.test(status ?? "");

type Transcript = { transcription: string | null; status: string | null };

export function VisitSpeakersProvider({
  visitPublicId,
  transcripts,
  refresh,
  children,
}: {
  visitPublicId: string | null;
  transcripts: Transcript[];
  refresh: () => Promise<void> | void;
  children: React.ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const scope = useVisitScope();
  const [identifying, setIdentifying] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const enabled = AppConfig.enableSpeakerRoles && !!visitPublicId && !!scope;
  const pending =
    enabled &&
    transcripts.some(
      (item) =>
        !isBusy(item.status) &&
        hasUnidentifiedSpeakers(item.transcription ?? ""),
    );
  const key = `${scope?.uid}:${visitPublicId}:${transcripts.map((item) => item.transcription?.length ?? 0).join(",")}`;

  useEffect(() => {
    if (!pending || !visitPublicId || attempted.has(key)) return;
    attempted.add(key);
    setIdentifying(true);
    void referenceClient
      .identifyVisitSpeakers(
        { params: { public_id: visitPublicId }, query: {} },
        { context: { expectedUserUid: scope?.uid } },
      )
      .then(async (result) => {
        visitSpeakersSchema.parse(result);
        await refresh();
      })
      // Speakers stay as "Speaker N"; the clinician can still set roles.
      .catch(() => undefined)
      .finally(() => {
        if (mounted.current) setIdentifying(false);
      });
  }, [pending, visitPublicId, key, scope?.uid, refresh]);

  const setRole = useCallback<NonNullable<VisitSpeakersValue["setRole"]>>(
    async ({ recordingPublicId, speaker, role, relation }) => {
      if (!visitPublicId) return;
      setSaving(`${recordingPublicId ?? "main"}:${speaker}`);
      try {
        visitSpeakersSchema.parse(
          await referenceClient.updateVisitSpeaker(
            {
              params: { public_id: visitPublicId },
              query: {},
              body: {
                recordingPublicId,
                speaker,
                role,
                ...(role === "caregiver" ? { relation: relation ?? null } : {}),
              },
            },
            { context: { expectedUserUid: scope?.uid } },
          ),
        );
        await refresh();
      } catch {
        toast({
          variant: "destructive",
          title: t("visit.speakers.saveFailed"),
        });
      } finally {
        if (mounted.current) setSaving(null);
      }
    },
    [visitPublicId, scope?.uid, refresh, t],
  );

  return (
    <VisitSpeakersContext.Provider
      value={enabled ? { identifying, saving, setRole } : null}
    >
      {children}
    </VisitSpeakersContext.Provider>
  );
}

// Fixed value without requests: for Storybook previews.
export function StaticVisitSpeakersProvider({
  value,
  children,
}: {
  value: VisitSpeakersValue;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <VisitSpeakersContext.Provider value={value}>
      {children}
    </VisitSpeakersContext.Provider>
  );
}
